'use client'

/**
 * Application frame (Issue #12946, #12947, #13731)
 *
 * A two-track CSS grid mounted once, in the root layout, above every route:
 *
 *     ┌─────────────────────────────┬──────────┐
 *     │ application pane            │ sidecar  │
 *     │ minmax(0, 1fr)              │ 440px    │
 *     └─────────────────────────────┴──────────┘
 *
 * The second track is `var(--app-sidecar-width, 0px)`, set from the frame
 * context. It is 0 whenever nothing is docked — chat-disabled orgs, hidden
 * routes, presentation mode, mobile — so the closed layout is a single
 * full-width track and nobody pays for blank sidecar space.
 *
 * Three things here are load-bearing:
 *
 *   1. **`children` is a prop, and the pane element it renders into never
 *      changes identity.** The frame re-renders on every dock/undock; React
 *      bails out of the `children` subtree because the element is
 *      referentially identical, so the current route does not remount. Do not
 *      wrap `children` in anything conditional, and do not key the pane on the
 *      sidecar state — either turns a width change into a full route remount
 *      that discards page scroll, form state and in-flight streaming.
 *   2. **The track width travels as a custom property, not as an inline
 *      `grid-template-columns`.** The template lives in `globals.css` next to
 *      the rest of the frame's CSS, which keeps the shorthand in one place and
 *      keeps the React side to a single, testable number.
 *   3. **The sidecar cell is never `aria-hidden`.** On mobile the reserved
 *      width is 0 while the chat is a full-screen dialog rendered from inside
 *      this cell; hiding the cell from assistive tech when the track is empty
 *      would hide that dialog.
 *
 * The pane carries `container-name: app-pane` always and `container-type`
 * only on request — see `useAppPaneQueryContainer` for why, and
 * docs/conventions/APP_FRAME.md for the migration contract.
 *
 * ## `data-app-pane-w` — the fourth thing, added by #12947
 *
 * The pane also publishes the breakpoints its MEASURED width has reached, as
 * a token list (`"sm md"`). The `pane-*` Tailwind variants in `globals.css`
 * are attribute selectors against it, which is how shared chrome becomes
 * responsive to the application pane on every route at once — without
 * `container-type`, and therefore without making the pane a containing block
 * for the ~30 non-portaled `fixed` overlays beneath it. `container-type`
 * remains the opt-in route-level tool it was; this is the always-on one.
 *
 * ## `--ai-fab-clearance` — the vertical half, added by #13731
 *
 * The same trick on the other axis. A `position: fixed` control at the BOTTOM
 * of the viewport (today only `AiChatFab`) reserves its occupied band through
 * `useFabReservation`, and this element publishes it as a custom property
 * plus a `data-fab-clearance` attribute. A page-level scrollport consumes it
 * with `.ai-fab-clear-bottom` (`globals.css`), which falls back to `0px` — so
 * a route with no frame above it, a chat-disabled org and every route where
 * `shouldHideAiChat` suppresses the FAB all render the pre-#13731 layout by
 * construction rather than by a matching branch. It is written from the SAME
 * `style` object as `--app-sidecar-width` so the frame keeps one writer for
 * both axes.
 *
 * Publishing it here rather than imperatively in the `ResizeObserver`
 * callback keeps a single writer for the attribute: `paneWidth` is already
 * React state, so an imperative write would be clobbered by the next render.
 * The cost is a re-render of this component per resize tick, and it is a
 * re-render of two `<div>`s — `children` and `sidecar` are props, so React
 * bails out of both subtrees on element identity. That bail-out is the same
 * property invariant 1 rests on, and
 * `tests/unit/components/app-frame/app-pane-width-attribute-12947.test.tsx`
 * asserts a resize does not remount the route.
 */

import type { ReactNode } from 'react'
import {
  APP_PANE_CONTAINER_NAME,
  useAppFrame,
  useAppPaneWidth,
} from './app-frame-context'
import { appPaneWidthTokens } from './app-pane-breakpoints'

export function AppFrame({
  children,
  sidecar,
}: {
  children: ReactNode
  sidecar: ReactNode
}) {
  const {
    sidecarWidth,
    fabClearance,
    recordingBarClearance,
    registerPane,
    isQueryContainerEnabled,
  } = useAppFrame()
  const paneWidth = useAppPaneWidth()

  return (
    <div
      data-app-frame=""
      data-sidecar-width={sidecarWidth}
      // Published beside the sidecar width, and asserted from e2e as the
      // cheap "did this route reserve anything" probe — a FAB-suppressed
      // route reads "0" here rather than needing a screenshot to prove it
      // gained no dead space (#13731).
      data-fab-clearance={fabClearance}
      data-recording-bar-clearance={recordingBarClearance}
      style={
        {
          '--app-sidecar-width': `${sidecarWidth}px`,
          '--ai-fab-clearance': `${fabClearance}px`,
          '--recording-bar-clearance': `${recordingBarClearance}px`,
        } as React.CSSProperties
      }
    >
      <div
        // A stable callback ref that writes to a REF and returns its own
        // cleanup — never a `useState` setter. See `registerPane` in
        // app-frame-context.tsx for the loop that shape caused.
        ref={registerPane}
        data-app-pane={APP_PANE_CONTAINER_NAME}
        data-app-pane-query-container={isQueryContainerEnabled ? 'on' : 'off'}
        data-app-pane-w={appPaneWidthTokens(paneWidth)}
        data-testid="app-pane"
      >
        {children}
      </div>
      <div data-app-sidecar="">{sidecar}</div>
    </div>
  )
}
